import type { Building } from "building-types/building/buildingTypes";
import type { GroupByEntityTypeTable } from "building-types/entities/groupByEntityTypeTable";
import type { Group } from "building-types/groups/group";
import type { GroupableEntityType } from "building-types/handles/docEntityHandleTypes";
import { DocEntityPrefixToDocEntityTypes } from "building-types/handles/docEntityPrefix";
import type { RWTable } from "common/data/table";
import stableHash from "common/utils/stableHash";
import { SystemGroups } from "doc/docSystemGroups";
import { parseEntityInfoFromHandleHash } from "doc/entity/docEntityHandleHash";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

type GenerationResult = Pick<Building, "groups" | "entityDisabledByGroup" | "groupByEntityType">;

function select({ doc, optionChoices }: Building) {
  const { groups, entityGroups } = doc;
  return {
    groups,
    entityGroups,
    optionChoices: resolveOptionsInFormula(groups, doc, optionChoices),
  };
}

/**
 * The result of generateGroups is used as a cache key in recursivelyDisableByGroup.
 * We cache its result to ensure the cache key is stable.
 */

const generateGroupsCache: { hash: string; generationResult: GenerationResult }[] = [];
const MAX_CACHE_LENGTH = 10;

function generateGroups(args: ReturnType<typeof select>): GenerationResult {
  const hash = stableHash(args);
  const elem = generateGroupsCache.find((x) => x.hash === hash);
  if (elem) {
    return elem.generationResult;
  }

  if (generateGroupsCache.length === MAX_CACHE_LENGTH) {
    generateGroupsCache.shift();
  }

  const { entityGroups, optionChoices } = args;
  const groups = { ...(evalFormulas(args.groups, (v) => optionChoices[v]) as RWTable<Group>) };

  // Add system groups
  for (const groupId in SystemGroups) {
    groups[groupId] = SystemGroups[groupId];
  }

  const groupByEntityType: GroupByEntityTypeTable = {};

  const entityDisabledByGroup: RWTable<boolean> = {};

  for (const hash in entityGroups) {
    const groupId = entityGroups[hash];
    const group = groups[groupId];

    const result = parseEntityInfoFromHandleHash(hash);

    if (!result) {
      continue;
    }
    const { entityId, entityTypePrefix } = result;

    const groupableEntityTypes = DocEntityPrefixToDocEntityTypes[entityTypePrefix] as GroupableEntityType[];
    if (!groupableEntityTypes?.length) {
      throw new Error("groupable entity types must be at least one");
    }

    const entityType = groupableEntityTypes[0];

    const groupResult: RWTable<string> = groupByEntityType[entityType] || {};
    groupResult[entityId] = groupId;
    groupByEntityType[entityType] = groupResult;

    if (!group?.disabled) {
      continue;
    }

    entityDisabledByGroup[entityId] = true;
  }

  const generationResult = { groups, entityDisabledByGroup, groupByEntityType };
  generateGroupsCache.push({ hash, generationResult });

  return generationResult;
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
});

export default memo(select, generateGroups, compare);
